
/**
 * Helper to find a node by ID
 */
const findNode = (nodes, id) => nodes.find(n => n.id === id);

/**
 * Helper to normalize schema columns
 */
const normalizeSchema = (schema) => {
    if (!Array.isArray(schema)) return [];
    return schema.map(col => {
        const name = col.name || col.columnName || 'unknown_column';
        return {
            ...col,
            name: name,
            columnName: name // Ensure both exist
        };
    });
};

/**
 * Recursive function to calculate the OUTPUT schema of a node.
 * 
 * @param {string} nodeId - The ID of the node to calculate output schema for
 * @param {Array} nodes - All nodes in the flow
 * @param {Array} edges - All edges in the flow
 * @param {Set} visited - Set of visited node IDs to prevent infinite loops
 * @returns {Array} The calculated output schema (array of column definitions)
 */
export const calculateNodeOutputSchema = (nodeId, nodes, edges, visited = new Set()) => {
    // Prevent infinite loops in cyclic graphs
    if (visited.has(nodeId)) return [];
    visited.add(nodeId);

    const currentNode = findNode(nodes, nodeId);
    if (!currentNode) return [];

    const nodeType = currentNode.data?.nodeType || currentNode.data?.type;
    const config = currentNode.data?.config || {};

    // --- 1. Source Nodes (Base Case) ---
    if (['excel_input', 'csv_input'].includes(nodeType)) {
        const rawSchema = config.schema || currentNode.data?.schema || [];
        return normalizeSchema(rawSchema);
    }
    
    if (nodeType === 'table') {
        if (config.selectedSchema && config.selectedSchema.length > 0) {
            return normalizeSchema(config.selectedSchema);
        }
    }

    // --- 2. Get Parent Schema ---
    const incomingEdges = edges.filter(e => e.target === nodeId);
    
    if (incomingEdges.length === 0) {
        return [];
    }

    // Handle Join/Merge nodes specially
    if (['join', 'merge', 'merge_flow'].includes(nodeType)) {
        
        if (nodeType === 'join') {
            // Identify Left and Right inputs
            // We try to match handles if possible, otherwise assume order or specific logic
            let leftEdge = incomingEdges.find(e => e.targetHandle === 'left' || e.targetHandle === 'dataset_left');
            let rightEdge = incomingEdges.find(e => e.targetHandle === 'right' || e.targetHandle === 'dataset_right');

            // Fallback if handles aren't named 'left'/'right' (e.g. generic handles)
            if (!leftEdge && incomingEdges.length > 0) leftEdge = incomingEdges[0];
            if (!rightEdge && incomingEdges.length > 1) rightEdge = incomingEdges[1];

            const leftSchema = leftEdge ? calculateNodeOutputSchema(leftEdge.source, nodes, edges, new Set(visited)) : [];
            const rightSchema = rightEdge ? calculateNodeOutputSchema(rightEdge.source, nodes, edges, new Set(visited)) : [];

            const combined = [];
            const seen = new Set();

            // Process Left Schema
            leftSchema.forEach(col => {
                // Check if column is selected (if selection is configured)
                if (config.leftColumns && config.leftColumns.length > 0 && !config.leftColumns.includes(col.columnName)) {
                    return;
                }

                const prefix = config.leftPrefix || '';
                const newName = prefix + col.columnName;
                
                if (!seen.has(newName)) {
                    seen.add(newName);
                    combined.push({ ...col, name: newName, columnName: newName });
                }
            });

            // Process Right Schema
            rightSchema.forEach(col => {
                // Check if column is selected (if selection is configured)
                if (config.rightColumns && config.rightColumns.length > 0 && !config.rightColumns.includes(col.columnName)) {
                    return;
                }

                const prefix = config.rightPrefix || '';
                const newName = prefix + col.columnName;

                if (!seen.has(newName)) {
                    seen.add(newName);
                    combined.push({ ...col, name: newName, columnName: newName });
                }
            });

            return combined;
        }
        
        // Merge / Merge Flow: Union of all inputs
        // Usually merge expects identical schemas, but we'll union all unique columns found
        const schemas = incomingEdges.map(edge => calculateNodeOutputSchema(edge.source, nodes, edges, new Set(visited)));
        const combined = [];
        const seen = new Set();
        
        schemas.forEach(schema => {
            schema.forEach(col => {
                if (!seen.has(col.columnName)) {
                    seen.add(col.columnName);
                    combined.push(col);
                }
            });
        });
        return combined;
    }

    // For single-input nodes, get the parent's schema
    const parentId = incomingEdges[0].source;
    const parentSchema = calculateNodeOutputSchema(parentId, nodes, edges, new Set(visited));

    // --- 3. Apply Node Transformations ---
    switch (nodeType) {
        // Structure Preserving Nodes
        case 'filter_rows':
        case 'if_else':
        case 'sort':
        case 'limit':
        case 'slice':
        case 'shuffle':
        case 'distinct':
        case 'wait':
        case 'clean_data':
        case 'remove_duplicates':
        case 'split':
        case 'csv_writer': // Writer passes through data
        case 'export_excel':
            return parentSchema;

        // Column Selection / Filtering
        case 'filter_columns':
        case 'select_columns':
        case 'remove_column':
            if (config.action === 'include' && config.columns) {
                return parentSchema.filter(col => config.columns.includes(col.columnName));
            }
            if (config.action === 'exclude' && config.columns) {
                return parentSchema.filter(col => !config.columns.includes(col.columnName));
            }
            // If remove_column node specifically (often just a list of cols to remove)
            if (nodeType === 'remove_column' && config.columns) {
                 return parentSchema.filter(col => !config.columns.includes(col.columnName));
            }
            return parentSchema;

        // Add Column
        case 'add_column':
        case 'calculate_field':
            // Handle multiple columns (AddColumnNodeProperties)
            if (config.columns && Array.isArray(config.columns)) {
                let newSchema = [...parentSchema];
                
                config.columns.forEach(col => {
                    if (!col.name) return;
                    
                    const newColDef = {
                        name: col.name,
                        columnName: col.name,
                        dataType: col.dataType || 'string',
                        sampleValues: [],
                        nullable: true
                    };

                    // Remove existing column if it exists (to handle overwrite or move)
                    const existingIndex = newSchema.findIndex(c => c.columnName === col.name);
                    if (existingIndex >= 0) {
                        newSchema.splice(existingIndex, 1);
                    }

                    // Insert based on position
                    if (col.position === 'start') {
                        newSchema.unshift(newColDef);
                    } else if (col.position === 'after' && col.relativeTo) {
                        const relativeIndex = newSchema.findIndex(c => c.columnName === col.relativeTo);
                        if (relativeIndex >= 0) {
                            newSchema.splice(relativeIndex + 1, 0, newColDef);
                        } else {
                            // Fallback to end if relative column not found
                            newSchema.push(newColDef);
                        }
                    } else {
                        // Default to end
                        newSchema.push(newColDef);
                    }
                });
                return newSchema;
            }

            // Handle single column (Legacy or CalculateField)
            if (config.columnName) {
                const exists = parentSchema.some(col => col.columnName === config.columnName);
                if (exists) {
                    // If replacing, return schema with updated column (though type might change, we keep it simple)
                    return parentSchema; 
                }

                const newColName = config.columnName;
                return [...parentSchema, {
                    name: newColName,
                    columnName: newColName,
                    dataType: config.dataType || 'string',
                    sampleValues: [],
                    nullable: true
                }];
            }
            return parentSchema;

        // Rename Column
        case 'rename_column':
            if (config.columnMappings && Array.isArray(config.columnMappings)) {
                return parentSchema.map(col => {
                    const mapping = config.columnMappings.find(m => m.oldName === col.columnName);
                    if (mapping && mapping.newName) {
                        return { ...col, name: mapping.newName, columnName: mapping.newName };
                    }
                    return col;
                });
            }
            // Legacy support for single mapping or object mapping
            if (config.mapping) {
                return parentSchema.map(col => {
                    const newName = config.mapping[col.columnName];
                    if (newName) {
                        return { ...col, name: newName, columnName: newName };
                    }
                    return col;
                });
            }
            return parentSchema;

        // Map Node
        case 'map':
            let baseSchema = config.keepUnmapped !== false ? [...parentSchema] : [];
            
            if (config.mappings && Array.isArray(config.mappings)) {
                config.mappings.forEach(mapping => {
                    if (!mapping.target) return;
                    
                    const existingIndex = baseSchema.findIndex(col => col.columnName === mapping.target);
                    const newCol = {
                        name: mapping.target,
                        columnName: mapping.target,
                        dataType: 'string', // Could be inferred but defaulting to string
                        nullable: true
                    };

                    if (existingIndex >= 0) {
                        // Update existing
                        baseSchema[existingIndex] = newCol;
                    } else {
                        // Add new
                        baseSchema.push(newCol);
                    }
                });
            }
            return baseSchema;

        // Window Function
        case 'window_function':
            if (config.resultColumn) {
                const exists = parentSchema.some(col => col.columnName === config.resultColumn);
                if (!exists) {
                    return [...parentSchema, {
                        name: config.resultColumn,
                        columnName: config.resultColumn,
                        dataType: 'float', // Most window functions return numbers
                        nullable: true
                    }];
                }
            }
            return parentSchema;

        // Aggregation / Group By
        case 'group_by':
            const aggSchema = [];
            
            // Handle both 'groupBy' (AggregationNode) and 'groupByColumns' (GroupByNode legacy)
            const groupByCols = config.groupBy || config.groupByColumns || [];
            
            if (groupByCols) {
                groupByCols.forEach(colName => {
                    const originalCol = parentSchema.find(c => c.columnName === colName);
                    aggSchema.push(originalCol || { name: colName, columnName: colName, dataType: 'string' });
                });
            }
            
            if (config.aggregations) {
                config.aggregations.forEach(agg => {
                    const name = agg.alias || `${agg.function}_${agg.column}`;
                    aggSchema.push({
                        name: name,
                        columnName: name,
                        dataType: 'float'
                    });
                });
            }
            
            return aggSchema;

        // Pivot
        case 'pivot':
            if (config.operation === 'unpivot') {
                const unpivotSchema = [];
                // ID Columns
                const idCols = config.idColumns || [];
                idCols.forEach(colName => {
                    const originalCol = parentSchema.find(c => c.columnName === colName);
                    unpivotSchema.push(originalCol || { name: colName, columnName: colName, dataType: 'string' });
                });
                
                // Variable and Value columns
                const varName = config.variableName || 'variable';
                const valName = config.valueName || 'value';
                
                unpivotSchema.push({ name: varName, columnName: varName, dataType: 'string' });
                unpivotSchema.push({ name: valName, columnName: valName, dataType: 'string' }); // Type depends on value columns
                
                return unpivotSchema;
            } else {
                // Pivot
                const pivotSchema = [];
                // Index Columns
                const indexCols = config.indexColumns || [];
                indexCols.forEach(colName => {
                    const originalCol = parentSchema.find(c => c.columnName === colName);
                    pivotSchema.push(originalCol || { name: colName, columnName: colName, dataType: 'string' });
                });
                
                // We cannot know the dynamic columns created by pivot values without data.
                // We return just the index columns as a baseline.
                return pivotSchema;
            }

        case 'table':
            if (config.selectedSchema && config.selectedSchema.length > 0) {
                return normalizeSchema(config.selectedSchema);
            }
            return parentSchema;

        default:
            return parentSchema;
    }
};
